import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, SimpleGrid, Stack, Text, VStack, useToast } from '@chakra-ui/react';
import { ArrowLeft, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { editCompany, getCompany, saveCompany } from '../hooks/companies';
import { Company } from '../utils/types';
import { CompanyFormSections, emptyCompanyDraft, toCompanyDraft } from './companies/components/CompanyFormSections';
import {
  CompanyField,
  CompanyHero,
  CompanyPrimaryButton,
  CompanySectionCard,
  CompanyShell,
  CompanyStatusBadge,
} from './companies/components/companyUi';

const normalizeCompany = (data: unknown): Company | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  return data as Company;
};

export default function CompanyEditorPage() {
  const { orgId, companyId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isEdit = Boolean(companyId);

  const [draft, setDraft] = useState(emptyCompanyDraft);
  const [initialDraft, setInitialDraft] = useState(emptyCompanyDraft);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompany = async () => {
      if (!orgId || !companyId) {
        return;
      }

      setLoading(true);
      const response = await getCompany(orgId, companyId);
      const company = normalizeCompany(response.data);

      if (!response.success || !company) {
        setError('Could not load company details.');
        setLoading(false);
        return;
      }

      const nextDraft = toCompanyDraft(company);
      setDraft(nextDraft);
      setInitialDraft(nextDraft);
      setLoading(false);
    };

    fetchCompany();
  }, [companyId, orgId]);

  const isComplete = Boolean(draft.website && (draft.email || draft.phone));
  const hasChanges = JSON.stringify(draft) !== JSON.stringify(initialDraft);

  const summary = useMemo(
    () => [
      { label: 'Profile status', value: isComplete ? 'Complete' : 'Needs details' },
      { label: 'Website', value: draft.website || 'Missing' },
      { label: 'Contact', value: draft.email || draft.phone || 'Missing' },
    ],
    [draft.email, draft.phone, draft.website, isComplete],
  );

  const handleChange = (field: keyof typeof draft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!orgId) {
      return;
    }

    if (!draft.name.trim()) {
      toast({
        title: 'Company name is required',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (isEdit && !hasChanges) {
      toast({
        title: 'No changes detected',
        status: 'info',
        duration: 2200,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    setError(null);
    const payload = {
      id: draft.id,
      name: draft.name.trim(),
      website: draft.website.trim(),
      email: draft.email.trim(),
      phone: draft.phone.trim(),
      address: draft.address.trim(),
    };

    const response = isEdit && companyId
      ? await editCompany(orgId, companyId, payload)
      : await saveCompany(orgId, payload);

    setSaving(false);

    if (!response.success || !response.data) {
      const message =
        typeof response.data === 'string'
          ? response.data
          : 'The company could not be saved.';
      setError(message);
      toast({
        title: isEdit ? 'Could not update company' : 'Could not create company',
        description: message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const savedCompany = normalizeCompany(response.data);
    if (savedCompany) {
      const nextDraft = toCompanyDraft(savedCompany);
      setDraft(nextDraft);
      setInitialDraft(nextDraft);
    }

    toast({
      title: isEdit ? 'Company updated' : 'Company created',
      status: 'success',
      duration: 2500,
      isClosable: true,
    });

    const resolvedId = savedCompany?.id || companyId;
    if (resolvedId) {
      navigate(`/organization/${orgId}/manage-companies/${resolvedId}`);
    } else {
      navigate(`/organization/${orgId}/manage-companies`);
    }
  };

  if (loading) {
    return (
      <CompanyShell compact>
        <CompanySectionCard title="Loading company" description="Please wait while the company details are loading.">
          <Text color="gray.500">Loading company details...</Text>
        </CompanySectionCard>
      </CompanyShell>
    );
  }

  return (
    <CompanyShell compact>
      <CompanyHero
        title={isEdit ? draft.name || 'Edit company' : 'Add company'}
        description={
          isEdit
            ? 'Update the company record and keep contact and profile details accurate for the team.'
            : 'Create a company record with the core details your team needs for CRM work.'
        }
        primaryAction={
          <CompanyPrimaryButton
            leftIcon={<Save size={18} />}
            onClick={handleSubmit}
            isLoading={saving}
            loadingText="Saving"
          >
            {isEdit ? 'Save Changes' : 'Create Company'}
          </CompanyPrimaryButton>
        }
        secondaryAction={
          <Button
            borderRadius="16px"
            variant="outline"
            leftIcon={<ArrowLeft size={16} />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        }
        eyebrow={isEdit ? 'Edit company' : 'Add company'}
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <CompanyFormSections draft={draft} onChange={handleChange} />
        </Stack>

        <Stack spacing={6}>
          <CompanySectionCard
            title="Save"
            description="Review the profile state and save when you are ready."
          >
            <VStack align="stretch" spacing={3}>
              <CompanyStatusBadge complete={isComplete} />
              <CompanyPrimaryButton
                leftIcon={<Save size={16} />}
                onClick={handleSubmit}
                isLoading={saving}
                loadingText="Saving"
              >
                {isEdit ? 'Save Changes' : 'Create Company'}
              </CompanyPrimaryButton>
              <Button borderRadius="16px" variant="ghost" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              {error ? (
                <Text color="red.500" fontSize="sm">
                  {error}
                </Text>
              ) : null}
            </VStack>
          </CompanySectionCard>

          <CompanySectionCard
            title="Profile summary"
            description="A quick view of the most important company details."
          >
            <Stack spacing={4}>
              {summary.map((item) => (
                <CompanyField key={item.label} label={item.label} value={item.value} />
              ))}
            </Stack>
          </CompanySectionCard>

          <CompanySectionCard
            title="Recommended"
            description="These details make the record easier to use across the CRM."
          >
            <Stack spacing={3}>
              <Text fontSize="sm" color="gray.600">
                Add a website so the company is easier to recognize in lists and forms.
              </Text>
              <Text fontSize="sm" color="gray.600">
                Add at least one contact channel so reps can take action without hunting for details.
              </Text>
            </Stack>
          </CompanySectionCard>
        </Stack>
      </SimpleGrid>
    </CompanyShell>
  );
}
